import type { ProjectData } from './persistence';

/**
 * Typisierter Client für die CampSketch-REST-API (Konten + Projekt-Datenbank).
 * Sitzung läuft über das HttpOnly-Cookie des Servers — hier nichts zu verwalten.
 */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export interface ProjectMeta {
  id: string;
  name: string;
  thumbnail: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectRecord extends ProjectMeta {
  data: ProjectData;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
  }

  get unauthorized(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const res = await fetch(path, {
    method: init?.method ?? 'GET',
    headers: init?.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    credentials: 'same-origin'
  });
  if (res.status === 204) return undefined as T;
  const payload = (await res.json().catch(() => null)) as { error?: string } | null;
  if (!res.ok) throw new ApiError(res.status, payload?.error ?? `HTTP ${res.status}`);
  return payload as T;
}

export const api = {
  /** Angemeldeter Nutzer oder null (nicht eingeloggt). */
  async me(): Promise<SessionUser | null> {
    try {
      return (await request<{ user: SessionUser }>('/api/auth/me')).user;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  register: (email: string, password: string) => request<{ user: SessionUser }>('/api/auth/register', { method: 'POST', body: { email, password } }),
  login: (email: string, password: string) => request<{ user: SessionUser }>('/api/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  /** Liste der eigenen Projekte inkl. Gratis-Tarif-Limit (z. B. 3). */
  listProjects: () => request<{ projects: ProjectMeta[]; limit: number }>('/api/projects'),
  createProject: (name: string, data?: ProjectData, thumbnail?: string | null) =>
    request<{ project: ProjectMeta }>('/api/projects', { method: 'POST', body: { name, data, thumbnail } }),
  getProject: (id: string) => request<{ project: ProjectRecord }>(`/api/projects/${encodeURIComponent(id)}`),
  /** Teil-Update: nur übergebene Felder ändern sich (Name, Inhalt, Vorschaubild). */
  saveProject: (id: string, patch: { name?: string; data?: ProjectData; thumbnail?: string | null }) =>
    request<{ ok: true }>(`/api/projects/${encodeURIComponent(id)}`, { method: 'PUT', body: patch }),
  deleteProject: (id: string) => request<void>(`/api/projects/${encodeURIComponent(id)}`, { method: 'DELETE' })
};
